import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 91蜜桃官方导航`;
    }
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <section className="py-20">
            <div className="px-4 md:px-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">
                文章未找到
              </h1>
              <p className="text-sm text-foreground-500 mb-6">
                您要查看的文章不存在或已被移除
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const related = getRelatedArticles(article, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `https://91mitao.com/article/${article.slug}`,
    author: {
      '@type': 'Organization',
      name: '91蜜桃',
    },
    publisher: {
      '@type': 'Organization',
      name: '91蜜桃',
    },
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
    wordCount: article.content.length,
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 91蜜桃官方导航`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://91mitao.com/article/${article.slug}`}
        ogType="article"
      />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Article Header */}
        <section className="bg-background-100 py-8 md:py-12">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 leading-snug">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 次阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读时间 {article.readTime}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[10px] px-2 py-0.5 rounded bg-background-200/70 text-foreground-600 whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-10 md:py-14">
          <div className="px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <article
                className="prose prose-sm max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-lg prose-h3:text-base
                  prose-p:text-sm prose-p:text-foreground-700 prose-p:leading-relaxed
                  prose-ul:text-sm prose-ul:text-foreground-700
                  prose-ol:text-sm prose-ol:text-foreground-700
                  prose-li:my-1
                  prose-strong:text-foreground-900
                  prose-code:text-xs prose-code:bg-background-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                  prose-table:text-sm
                  prose-th:text-xs prose-th:font-semibold prose-th:bg-background-100 prose-th:px-3 prose-th:py-2
                  prose-td:text-xs prose-td:px-3 prose-td:py-2 prose-td:border-b prose-td:border-background-200/70"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Article Navigation */}
              <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row justify-between gap-4">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回 {article.categoryName}
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6">
              <div className="max-w-5xl mx-auto">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {related.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300/50 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                          {rel.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{rel.date}</span>
                      </div>
                      <h4 className="font-semibold text-sm text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-2 line-clamp-2">
                        {rel.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}